import React, { useMemo, useState } from 'react';
import { Button, Collapse, Tag, Typography } from 'antd';
import { UndoOutlined } from '@ant-design/icons';

import useDateFormat from '../../hooks/useDateFormat';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatRelativeToNow } from '../../utils/formatters';
import { actionColor, actionLabel, actorLabel, hasTagArrayChanges, historyTagRef, isItemDeletion, isRebuiltTagEntry, rulePassReason, TYPE_LABELS, withTagRuleWords } from '../../utils/history';
import { tagsById } from '../../utils/tags';
import useCollectionTags from '../../hooks/useCollectionTags';
import UserChip from '../users/UserChip';
import { HISTORY, LIGHTNING } from '../icons';
import ChangeTable from './ChangeTable';
import { HistoryTag } from './HistoryTagBadges';

const { Text } = Typography;

/**
 * One history entry, as a person reads it: who · what · when on the first
 * line, the server's one-sentence summary beneath, state tags (Derived,
 * Reverted, via API key), the field-level diff behind a collapse, and the
 * Revert door when the entry can be reverted by this caller.
 *
 * `onOpenItemHistory(entry)`, when a feed passes it, puts **View history** on
 * an item's DELETION (a delete, or an intake reject): the item has no page
 * any more, so its deletion in the feed is the door to the rest of its
 * history (DeletedItemHistoryModal).
 *
 * Nothing here computes a label — the API's describer did, once, for every
 * consumer (docs/SCHEMA.md § History).
 *
 * A TAG IS A TAG BADGE (ruled b54): a collection tag's own row names its tag
 * as its target badge, and the change table draws an item's tags as badges —
 * each the door to its tag page, a tombstone never (HistoryTagBadges). The
 * reader's vocabulary of the row's collection says which tags are still
 * there; it is asked for only by a row that names a tag.
 */
export default function HistoryEntry({ entry: stored, onRevert, onOpenItemHistory, showTarget = true, compact = false }) {
	useDateFormat();
	const [open, setOpen] = useState([]);
	// The tags a row names (an item's `tagIds` / `tagAutoIds`, a collection
	// tag's own row, a merge's survivor) are drawn from the vocabulary of the
	// row's collection, fetched once per collection and shared by every row
	// (hooks/useCollectionTags) — and only when a row actually names one.
	const namesTags = hasTagArrayChanges(stored) || isRebuiltTagEntry(stored);
	const { tags, loaded: tagsLoaded } = useCollectionTags(stored?.scope?.collection_id, { enabled: namesTags });
	const tagIndex = useMemo(() => tagsById(tags), [tags]);
	if (!stored) return null;
	// An automatic tag's rule reads in words, never as an automation's id (T6a).
	const entry = withTagRuleWords(stored);
	const revertable = Boolean(entry.revertable);

	const changes = Array.isArray(entry.changes) ? entry.changes : [];
	const when = entry.at ? formatDisplayDateTime(entry.at) : '';
	const opensItemHistory = typeof onOpenItemHistory === 'function' && isItemDeletion(entry);
	const targetType = TYPE_LABELS[entry.target?.type] || entry.target?.type || '';
	// Why an automatic tag's pass ran, when no person did it (T6b).
	const passReason = rulePassReason(entry);

	return (
		<article className={['history-entry', compact ? 'history-entry--compact' : ''].filter(Boolean).join(' ')} data-action={entry.action}>
			<div className="history-entry__head">
				<Actor actor={entry.actor} />
				<Tag color={actionColor(entry)} className="history-entry__action">{actionLabel(entry.action, entry)}</Tag>
				{showTarget && entry.target?.label ? (
					<span className="history-entry__target" title={targetType}>
						{entry.target.card || targetType ? <span className="micro-label history-entry__kicker">{entry.target.card || targetType}</span> : null}
						{isRebuiltTagEntry(entry) && entry.target.id ? (
							<HistoryTag entry={historyTagRef(entry.target.id, entry.target.label, tagIndex, { loaded: tagsLoaded })} />
						) : <span className="history-entry__title">{entry.target.label}</span>}
					</span>
				) : null}
				<time className="history-entry__time" dateTime={entry.at} title={when}>{formatRelativeToNow(entry.at)}</time>
			</div>

			{entry.summary ? <p className="history-entry__summary">{entry.summary}</p> : null}

			<div className="history-entry__tags">
				{entry.derived ? <Tag>Derived</Tag> : null}
				{passReason ? <Tag>{passReason}</Tag> : null}
				{entry.revertedBy ? <Tag color="purple">Reverted</Tag> : null}
				{entry.revert ? <Tag color="purple">Revert of an earlier change</Tag> : null}
				{entry.actor?.type === 'api-key' ? <Tag>via API key{entry.actor.keyName ? ` · ${entry.actor.keyName}` : ''}</Tag> : null}
				{entry.actor?.type === 'automation' && entry.actor.userId ? <Tag>triggered by a save</Tag> : null}
				{entry.oversize ? <Tag color="gold">values not kept (too large)</Tag> : null}
			</div>

			{changes.length ? (
				<Collapse
					ghost
					size="small"
					className="history-entry__changes"
					expandIconPosition="end"
					activeKey={open}
					onChange={keys => setOpen(keys)}
					items={[{
						key: 'changes',
						label: <span className="history-entry__changes-label">{changes.length === 1 ? '1 field' : `${changes.length} fields`}</span>,
						children: <ChangeTable changes={changes} tagIndex={tagIndex} tagsLoaded={tagsLoaded} />,
					}]}
				/>
			) : null}

			{(revertable && typeof onRevert === 'function') || opensItemHistory ? (
				<div className="history-entry__actions">
					{opensItemHistory ? (
						<Button
							size="small"
							icon={<HISTORY />}
							aria-label={`View history of ${entry.target?.label || 'this item'}`}
							onClick={() => onOpenItemHistory(entry)}
						>
							View history
						</Button>
					) : null}
					{revertable && typeof onRevert === 'function' ? (
						<Button size="small" icon={<UndoOutlined />} onClick={() => onRevert(stored)}>
							Revert…
						</Button>
					) : null}
				</div>
			) : null}
		</article>
	);
}

/** Who did it: a user chip when there is a person, a phrase otherwise. */
function Actor({ actor }) {
	if (!actor) return <Text type="secondary">Someone</Text>;
	if (actor.type === 'automation') {
		return (
			<span className="history-entry__actor history-entry__actor--automation">
				<LIGHTNING aria-hidden="true" />
				<span>{actorLabel(actor)}</span>
			</span>
		);
	}
	if (actor.user) {
		return (
			<span className="history-entry__actor">
				<UserChip user={actor.user} size={20} />
			</span>
		);
	}
	return <span className="history-entry__actor"><Text>{actorLabel(actor)}</Text></span>;
}
